import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTourAutoStart } from '@/hooks/useTourAutoStart';
import { adminUsersTourId, adminUsersSteps } from '@/tours/admin';
import {
  Users,
  Shield,
  ShieldCheck,
  UserCog,
  UserCheck,
  Lock,
  Unlock,
  Mail,
  Phone,
  Calendar,
  Hash,
  Building2,
  KeyRound,
} from 'lucide-react';
import {
  Card,
  EmptyState,
  Pagination,
  SearchInput,
  Select,
  LoadingSpinner,
  ErrorMessage,
  Table,
  Button,
  Modal,
} from '@/components/ui';
import { adminApi } from '@/services/api/adminApi';
import { UserRole, UserRoleLabels } from '@/types/enums';
import { cn } from '@/lib/utils';
import toast from 'react-hot-toast';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const ITEMS_PER_PAGE = 20;

interface AdminUserRecord {
  id: number;
  email: string;
  full_name: string;
  role: string;
  branch_id: number | null;
  phone: string | null;
  atomi_id: number | null;
  must_change_password: boolean;
  is_active: boolean;
  created_at: string;
}

const ROLE_BADGE_COLORS: Record<string, string> = {
  ADMIN: 'bg-purple-100 text-purple-700',
  MANAGER: 'bg-blue-100 text-blue-700',
  EMPLOYEE: 'bg-teal-100 text-teal-700',
  CUSTOMER: 'bg-gray-100 text-gray-600',
};

const ROLE_ICONS: Record<string, typeof Shield> = {
  ADMIN: ShieldCheck,
  MANAGER: UserCog,
  EMPLOYEE: UserCheck,
  CUSTOMER: Users,
};

const roleFilterOptions = [
  { value: '', label: 'Все роли' },
  { value: UserRole.ADMIN, label: UserRoleLabels.ADMIN },
  { value: UserRole.MANAGER, label: UserRoleLabels.MANAGER },
  { value: UserRole.EMPLOYEE, label: UserRoleLabels.EMPLOYEE },
  { value: UserRole.CUSTOMER, label: UserRoleLabels.CUSTOMER },
];

const activeFilterOptions = [
  { value: '', label: 'Все' },
  { value: 'true', label: 'Активные' },
  { value: 'false', label: 'Заблокированные' },
];

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

const RoleBadge: React.FC<{ role: string }> = ({ role }) => {
  const colors = ROLE_BADGE_COLORS[role] ?? 'bg-gray-100 text-gray-600';
  const Icon = ROLE_ICONS[role] ?? Users;
  const label = UserRoleLabels[role as keyof typeof UserRoleLabels] ?? role;
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium', colors)}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

const ActiveBadge: React.FC<{ isActive: boolean }> = ({ isActive }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
      isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700',
    )}
  >
    {isActive ? (
      <>
        <Unlock className="w-3 h-3" />
        Активен
      </>
    ) : (
      <>
        <Lock className="w-3 h-3" />
        Заблокирован
      </>
    )}
  </span>
);

const UserDetailModal: React.FC<{
  user: AdminUserRecord | null;
  isOpen: boolean;
  onClose: () => void;
}> = ({ user, isOpen, onClose }) => {
  if (!user) return null;

  const fields = [
    { icon: Hash, label: 'ID', value: String(user.id) },
    { icon: Mail, label: 'Email', value: user.email },
    { icon: Users, label: 'ФИО', value: user.full_name },
    { icon: Shield, label: 'Роль', value: null, badge: <RoleBadge role={user.role} /> },
    { icon: Building2, label: 'Филиал ID', value: user.branch_id != null ? String(user.branch_id) : '—' },
    { icon: Phone, label: 'Телефон', value: user.phone ?? '—' },
    { icon: Hash, label: 'Atomi ID', value: user.atomi_id != null ? String(user.atomi_id) : '—' },
    { icon: KeyRound, label: 'Сменить пароль', value: user.must_change_password ? 'Да' : 'Нет' },
    { icon: Lock, label: 'Статус', value: null, badge: <ActiveBadge isActive={user.is_active} /> },
    { icon: Calendar, label: 'Зарегистрирован', value: formatDateTime(user.created_at) },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Профиль пользователя" size="md">
      <div className="space-y-3">
        {fields.map(({ icon: Icon, label, value, badge }) => (
          <div key={label} className="flex items-center gap-3 py-1.5 border-b border-gray-50 last:border-0">
            <Icon className="w-4 h-4 text-gray-400 shrink-0" />
            <span className="text-sm text-gray-500 w-32 shrink-0">{label}</span>
            {badge ?? <span className="text-sm text-gray-900">{value}</span>}
          </div>
        ))}
      </div>
    </Modal>
  );
};

const AdminUsersPage: React.FC = () => {
  useTourAutoStart({ tourId: adminUsersTourId, steps: adminUsersSteps });
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [detailUser, setDetailUser] = useState<AdminUserRecord | null>(null);

  const filtersKey = JSON.stringify({ search, role: roleFilter, is_active: activeFilter, page });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'users', filtersKey],
    queryFn: () =>
      adminApi.getUsers({
        search: search || undefined,
        role: roleFilter || undefined,
        is_active: activeFilter === '' ? undefined : activeFilter === 'true',
        page,
        limit: ITEMS_PER_PAGE,
      }),
  });

  const blockMutation = useMutation({
    mutationFn: (id: number) => adminApi.blockUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const unblockMutation = useMutation({
    mutationFn: (id: number) => adminApi.unblockUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleRoleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setRoleFilter(e.target.value);
    setPage(1);
  }, []);

  const handleActiveChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setActiveFilter(e.target.value);
    setPage(1);
  }, []);

  const handleBlock = useCallback(
    async (user: AdminUserRecord, e: React.MouseEvent) => {
      e.stopPropagation();
      try {
        await blockMutation.mutateAsync(user.id);
        toast.success(`${user.full_name} заблокирован`);
      } catch (err) {
        toast.error(extractErrorMessage(err, 'Не удалось заблокировать пользователя'));
      }
    },
    [blockMutation],
  );

  const handleUnblock = useCallback(
    async (user: AdminUserRecord, e: React.MouseEvent) => {
      e.stopPropagation();
      try {
        await unblockMutation.mutateAsync(user.id);
        toast.success(`${user.full_name} разблокирован`);
      } catch (err) {
        toast.error(extractErrorMessage(err, 'Не удалось разблокировать пользователя'));
      }
    },
    [unblockMutation],
  );

  const users = (data?.items ?? []) as AdminUserRecord[];
  const totalCount = data?.total ?? 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Пользователи</h1>
        <p className="text-sm text-gray-500 mt-1">
          Управление пользователями системы
          {totalCount > 0 && <span className="text-gray-400"> • {totalCount} всего</span>}
        </p>
      </div>

      <Card>
        <Card.Header>
          <div data-tour="admin:users-filters" className="flex flex-col sm:flex-row gap-3">
            <SearchInput
              value={search}
              onChange={handleSearchChange}
              placeholder="Поиск по имени или email..."
              className="flex-1"
            />
            <Select
              options={roleFilterOptions}
              value={roleFilter}
              onChange={handleRoleChange}
              className="sm:w-48"
            />
            <Select
              options={activeFilterOptions}
              value={activeFilter}
              onChange={handleActiveChange}
              className="sm:w-44"
            />
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          <div data-tour="admin:users-list">
          {isLoading ? (
            <LoadingSpinner size="lg" className="py-16" />
          ) : error ? (
            <ErrorMessage message="Не удалось загрузить пользователей" onRetry={() => refetch()} className="py-12" />
          ) : users.length === 0 ? (
            <EmptyState
              icon={<Users className="w-8 h-8" />}
              title="Пользователей не найдено"
              description={search || roleFilter || activeFilter ? 'Попробуйте изменить параметры поиска' : 'Пользователи пока не зарегистрированы'}
            />
          ) : (
            <>
              {/* Mobile */}
              <div className="md:hidden divide-y divide-gray-100">
                {users.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => setDetailUser(user)}
                    className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50/50 transition-colors text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">{user.full_name}</p>
                        <RoleBadge role={user.role} />
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="truncate">{user.email}</span>
                        <ActiveBadge isActive={user.is_active} />
                      </div>
                    </div>
                    <div className="shrink-0">
                      {user.is_active ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleBlock(user, e)}
                          loading={blockMutation.isPending}
                          className="!text-red-600 hover:!bg-red-50"
                        >
                          <Lock className="w-3.5 h-3.5" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleUnblock(user, e)}
                          loading={unblockMutation.isPending}
                          className="!text-emerald-600 hover:!bg-emerald-50"
                        >
                          <Unlock className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {/* Desktop */}
              <div className="hidden md:block">
                <Table>
                  <Table.Header>
                    <Table.Row>
                      <Table.Head>ФИО</Table.Head>
                      <Table.Head>Email</Table.Head>
                      <Table.Head>Роль</Table.Head>
                      <Table.Head>Atomi ID</Table.Head>
                      <Table.Head>Активен</Table.Head>
                      <Table.Head>Дата регистрации</Table.Head>
                      <Table.Head className="w-24">{' '}</Table.Head>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {users.map((user) => (
                      <Table.Row key={user.id} onClick={() => setDetailUser(user)}>
                        <Table.Cell className="font-medium text-gray-900">
                          {user.full_name}
                        </Table.Cell>
                        <Table.Cell>
                          <span className="text-gray-600">{user.email}</span>
                        </Table.Cell>
                        <Table.Cell>
                          <RoleBadge role={user.role} />
                        </Table.Cell>
                        <Table.Cell>
                          {user.atomi_id != null ? (
                            <span className="font-mono text-xs text-gray-600">{user.atomi_id}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <ActiveBadge isActive={user.is_active} />
                        </Table.Cell>
                        <Table.Cell>{formatDate(user.created_at)}</Table.Cell>
                        <Table.Cell>
                          {user.is_active ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleBlock(user, e)}
                              loading={blockMutation.isPending}
                              className="!text-red-600 hover:!bg-red-50"
                            >
                              <Lock className="w-3.5 h-3.5" />
                              Блок
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleUnblock(user, e)}
                              loading={unblockMutation.isPending}
                              className="!text-emerald-600 hover:!bg-emerald-50"
                            >
                              <Unlock className="w-3.5 h-3.5" />
                              Разблок
                            </Button>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            </>
          )}
          </div>
        </Card.Body>

        {totalPages > 1 && (
          <Card.Footer>
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </Card.Footer>
        )}
      </Card>

      <UserDetailModal
        user={detailUser}
        isOpen={detailUser !== null}
        onClose={() => setDetailUser(null)}
      />
    </div>
  );
};

export default AdminUsersPage;
